import type { ExpressionStudy } from "./expression-studies";
import type { Reading } from "./types";
import { computeReadingMetrics } from "./metrics";
import {
  contextualPromptIdentity,
  type WordCommandManifest,
} from "../app/word-command-prompts";

const WORD_PATTERN = /\p{L}+(?:[’'-]\p{L}+)*/gu;
const EXPRESSION_STANCE_IDS = ["direct", "tactful", "warm-authority", "restrained", "punchy"] as const;
const EXPRESSION_SIZE_COUNTS = { quick: 4, standard: 5, deep: 3 } as const;
const READING_LANGUAGES = ["English", "Hindi", "Punjabi"] as const;
const WORD_TRANSLATION_STATUSES = new Set([
  "editorially-verified",
  "human-verified",
  "model-assisted-unverified",
  "withheld",
]);
const WORD_PRONUNCIATION_STATUSES = new Set([
  "human-verified",
  "source-exact",
  "source-exact-variety-unspecified",
  "open-no-source-value",
  "open-related-form-only",
]);

export function normalizeContentWord(value: string) {
  return value.toLocaleLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
}

export function validateReading(reading: Reading, options: { requirePublished?: boolean } = {}) {
  const requirePublished = options.requirePublished ?? true;
  const errors: string[] = [];
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(reading.id)) errors.push("id must be a stable slug");
  if (!/^\d{4}-\d{2}-\d{2}\.\d+$/.test(reading.edition)) errors.push("edition must be date.sequence");
  if (reading.taxonomyVersion !== "taxonomy_v2") errors.push("taxonomy version is unsupported");
  if (reading.provenance !== "Verbalore original") errors.push("provenance must be explicit");
  if (requirePublished && reading.editorialState !== "published") errors.push("only editorially published works belong in the public catalog");
  if (!requirePublished && !["approved", "in_review", "published"].includes(reading.editorialState)) {
    errors.push("finished editorial work must be in review, approved, or published");
  }
  const receiptGates = new Set(reading.receipts.map((receipt) => receipt.gate));
  for (const gate of ["G0", "G1", "G2", "G3", "G4", "G5"] as const) {
    if (!receiptGates.has(gate)) errors.push(`missing ${gate} editorial receipt`);
  }
  if (reading.receipts.some((receipt) => receipt.edition !== reading.edition || !receipt.artifact.trim())) {
    errors.push("editorial receipts must bind to this edition and a maintained artifact");
  }
  if (reading.segments.length < 8) errors.push("reading needs at least eight maintained segments");
  if (JSON.stringify(reading).includes("—")) errors.push("em dashes are not allowed in authored reading content");

  const segmentIds = new Set<string>();
  const corpus = reading.segments.map((segment) => {
    if (segmentIds.has(segment.id)) errors.push(`duplicate segment id ${segment.id}`);
    segmentIds.add(segment.id);
    return segment.text;
  }).join(" ");
  if (reading.chapters?.length) {
    if (reading.chapters.length < 3) errors.push("chaptered reading needs at least three meaningful chapters");
    const chapterIds = new Set<string>();
    const openings = new Set<string>();
    reading.chapters.forEach((chapter, index) => {
      if (chapterIds.has(chapter.id)) errors.push(`duplicate chapter id ${chapter.id}`);
      if (openings.has(chapter.openingSegmentId)) errors.push(`duplicate chapter opening ${chapter.openingSegmentId}`);
      if (!segmentIds.has(chapter.openingSegmentId)) errors.push(`${chapter.id}: opening segment does not exist`);
      if (chapter.number !== index + 1) errors.push(`${chapter.id}: chapter number must follow reading order`);
      chapterIds.add(chapter.id);
      openings.add(chapter.openingSegmentId);
    });
    if (computeReadingMetrics(reading).estimatedMinutes < 25) errors.push("chaptered long work must offer at least 25 honest minutes with exploration");
  }
  const corpusWords = new Set((corpus.match(WORD_PATTERN) || []).map(normalizeContentWord));

  for (const [key, entry] of Object.entries(reading.words)) {
    if (normalizeContentWord(entry.word) !== key) errors.push(`${key}: key and word disagree`);
    if (!corpusWords.has(key)) errors.push(`${key}: word does not occur in reading`);
    if (!corpus.includes(entry.sentence)) errors.push(`${key}: source sentence is not exact`);
    if (entry.sentence && corpus.split(entry.sentence).length !== 2) errors.push(`${key}: source sentence must identify exactly one maintained context`);
    if (entry.alternateMeaning !== undefined && !entry.alternateMeaning.trim()) {
      errors.push(`${key}: alternate meaning cannot be empty`);
    }
    if (entry.usageNote !== undefined && !entry.usageNote.trim()) {
      errors.push(`${key}: usage note cannot be empty`);
    }
    if (entry.translationStatus) {
      const statuses = entry.translationStatus as Record<string, string>;
      const statusKeys = Object.keys(statuses).sort();
      if (statusKeys.join(",") !== [...READING_LANGUAGES].sort().join(",")) {
        errors.push(`${key}: translation status must cover exactly English, Hindi, and Punjabi`);
      }
      for (const language of READING_LANGUAGES) {
        const status = statuses[language];
        const value = entry.translations[language]?.trim() || "";
        if (!WORD_TRANSLATION_STATUSES.has(status)) {
          errors.push(`${key}: ${language} translation status is unsupported`);
        } else if (status === "withheld" && value) {
          errors.push(`${key}: withheld ${language} translation must not contain text`);
        } else if (status !== "withheld" && !value) {
          errors.push(`${key}: ${language} translation text is required for ${status}`);
        }
        if (
          language === "English"
          && !["editorially-verified", "human-verified"].includes(status)
        ) {
          errors.push(`${key}: English explanation must be editorially or human verified`);
        }
        if (language !== "English" && status === "editorially-verified") {
          errors.push(`${key}: non-English translation requires human verification or an explicit unverified status`);
        }
      }
    } else if (
      !entry.translations.English
      || !entry.translations.Hindi
      || !entry.translations.Punjabi
    ) {
      errors.push(`${key}: legacy translations require English, Hindi, and Punjabi text`);
    }
    if (entry.pronunciationEvidence) {
      const evidence = entry.pronunciationEvidence;
      if (!WORD_PRONUNCIATION_STATUSES.has(evidence.status)) {
        errors.push(`${key}: pronunciation evidence status is unsupported`);
      }
      if (!evidence.source.trim()) {
        errors.push(`${key}: pronunciation evidence needs a source statement`);
      }
      if (
        evidence.values.some((value) =>
          !value.value.trim()
          || (value.variety !== undefined && !value.variety.trim())
        )
      ) {
        errors.push(`${key}: pronunciation evidence values must be nonempty`);
      }
      if (
        evidence.status === "open-no-source-value"
        && evidence.values.length
      ) {
        errors.push(`${key}: open pronunciation evidence cannot contain values`);
      }
      if (
        evidence.status !== "open-no-source-value"
        && !evidence.values.length
      ) {
        errors.push(`${key}: pronunciation evidence status requires a source value`);
      }
    }
  }

  for (const word of reading.exercise.targetWords) {
    if (!reading.words[normalizeContentWord(word)]) errors.push(`exercise target ${word} has no lexical entry`);
  }
  if (!reading.taxonomy.outcomes.length || !reading.taxonomy.rhetoricalMoves.length) {
    errors.push("taxonomy needs outcomes and rhetorical moves");
  }
  if (!reading.taxonomy.mood.length || !reading.taxonomy.topics.length || !reading.taxonomy.sensitivityFlags.length) {
    errors.push("taxonomy v2 needs mood, topics, and sensitivity flags");
  }
  const metrics = computeReadingMetrics(reading);
  if (metrics.wordCount < 1 || metrics.estimatedMinutes < 1) errors.push("computed reading metrics are invalid");
  return errors;
}

export function assertValidCatalog(readings: Reading[]) {
  const ids = new Set<string>();
  for (const reading of readings) {
    if (ids.has(reading.id)) throw new Error(`Duplicate reading id: ${reading.id}`);
    ids.add(reading.id);
    const errors = validateReading(reading);
    if (errors.length) throw new Error(`${reading.id}: ${errors.join("; ")}`);
  }
  return readings;
}

function hasText(value: string) {
  return value.trim().length > 0;
}

function expressionText(study: ExpressionStudy) {
  return [
    study.title,
    ...study.craft,
    study.context,
    study.skill,
    study.situation,
    study.tangledDraft,
    study.intention,
    study.invariant,
    study.concept,
    study.practiceCue,
    ...study.stances.flatMap((stance) => [
      stance.label,
      stance.line,
      stance.landing,
      stance.protects,
      stance.tradeoff,
    ]),
  ].join(" ");
}

export function assertValidExpressionStudies(studies: ExpressionStudy[]) {
  const errors: string[] = [];
  const ids = new Set<string>();
  const numbers = new Set<string>();
  const sizeCounts: Record<ExpressionStudy["size"], number> = { quick: 0, standard: 0, deep: 0 };

  if (studies.length !== 12) errors.push(`expected 12 studies, received ${studies.length}`);

  for (const study of studies) {
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(study.id)) errors.push(`${study.id}: id must be a stable slug`);
    if (ids.has(study.id)) errors.push(`${study.id}: duplicate id`);
    if (numbers.has(study.number)) errors.push(`${study.id}: duplicate study number ${study.number}`);
    ids.add(study.id);
    numbers.add(study.number);
    sizeCounts[study.size] += 1;

    if (study.provenance !== "Original Verbalore") errors.push(`${study.id}: provenance must be Original Verbalore`);
    if (!/^\d{2}$/.test(study.number)) errors.push(`${study.id}: number must use two digits`);
    if (study.craft.length < 2 || study.craft.some((term) => !hasText(term))) errors.push(`${study.id}: craft terms are incomplete`);
    if (![
      study.title,
      study.context,
      study.skill,
      study.situation,
      study.tangledDraft,
      study.intention,
      study.invariant,
      study.concept,
      study.practiceCue,
    ].every(hasText)) errors.push(`${study.id}: authored study fields are incomplete`);
    if (study.minutes < 1 || study.minutes > 15) errors.push(`${study.id}: minutes must remain within the MVP study range`);
    if (study.stances.length !== EXPRESSION_STANCE_IDS.length) errors.push(`${study.id}: expected five stances`);

    const stanceIds = study.stances.map((stance) => stance.id);
    if (new Set(stanceIds).size !== stanceIds.length) errors.push(`${study.id}: stance ids must be unique`);
    for (const stanceId of EXPRESSION_STANCE_IDS) {
      if (!stanceIds.includes(stanceId)) errors.push(`${study.id}: missing ${stanceId} stance`);
    }
    for (const stance of study.stances) {
      if (![stance.label, stance.line, stance.landing, stance.protects, stance.tradeoff].every(hasText)) {
        errors.push(`${study.id}/${stance.id}: articulation is incomplete`);
      }
    }

    if (expressionText(study).includes("—")) errors.push(`${study.id}: em dashes are not allowed in authored expression content`);
  }

  for (const [size, expected] of Object.entries(EXPRESSION_SIZE_COUNTS)) {
    const actual = sizeCounts[size as ExpressionStudy["size"]];
    if (actual !== expected) errors.push(`expected ${expected} ${size} studies, received ${actual}`);
  }

  if (errors.length) throw new Error(`Expression Studio content invalid: ${errors.join("; ")}`);
  return studies;
}

export function assertValidWordCommandManifest(
  readings: Reading[],
  manifest: WordCommandManifest,
) {
  const errors: string[] = [];
  const corpus = new Map<string, { readingId: string; word: string }>();
  for (const reading of readings) {
    for (const word of Object.keys(reading.words)) {
      const contextId = `${reading.id}:${word}:v1`;
      if (corpus.has(contextId)) errors.push(`${contextId}: duplicate maintained contextual identity`);
      corpus.set(contextId, { readingId: reading.id, word });
    }
  }
  if (manifest.schemaVersion !== "vocabulary-review-v3") errors.push("unsupported manifest schema version");
  if (manifest.policyVersion !== "word-command-v2") errors.push("unsupported evidence policy version");
  if (manifest.reviewCorpus !== "contextual-senses-v2") errors.push("review corpus must be contextual-senses-v2");

  const packsByContext = new Map<string, WordCommandManifest["packs"][number]>();
  for (const pack of manifest.packs) {
    if (packsByContext.has(pack.contextId)) {
      errors.push(`${pack.contextId}: duplicate contextual prompt identity`);
    } else {
      packsByContext.set(pack.contextId, pack);
    }
  }
  const missing = [...corpus.keys()].filter((contextId) => !packsByContext.has(contextId));
  const extra = [...packsByContext.keys()].filter((contextId) => !corpus.has(contextId));
  if (missing.length) errors.push(`missing prompt packs: ${missing.join(", ")}`);
  if (extra.length) errors.push(`prompt packs outside declared corpus: ${extra.join(", ")}`);

  const contextualPromptIds = new Set<string>();
  for (const pack of manifest.packs) {
    const label = pack.contextId || pack.senseId || pack.word;
    const expected = corpus.get(pack.contextId);
    if (pack.senseId !== pack.contextId) errors.push(`${label}: senseId and contextId disagree`);
    if (expected) {
      if (pack.word !== expected.word) errors.push(`${label}: word does not bind the maintained context`);
      if (pack.readingId !== expected.readingId) errors.push(`${label}: readingId does not bind the maintained context`);
      if (pack.senseId !== `${expected.readingId}:${expected.word}:v1`) {
        errors.push(`${label}: senseId does not bind the maintained reading sense`);
      }
    }
    if (pack.checks.length < 3) errors.push(`${label}: at least three closed checks are required`);
    const forms = new Set(pack.checks.map((check) => check.assessmentForm));
    if (!forms.has("context_cloze") || !forms.has("sense_discrimination")) {
      errors.push(`${label}: checks need distinct context and sense-assessment forms`);
    }
    const normalizedStems = new Set<string>();
    const localPromptIds = new Set<string>();
    for (const check of pack.checks) {
      if (!/^[a-z0-9]+\.(?:recall|contrast)\.(?:primary|delayed)\.v\d+$/.test(check.id)) {
        errors.push(`${label}: invalid immutable check id ${check.id}`);
      }
      if (localPromptIds.has(check.id)) errors.push(`${label}: duplicate local prompt id ${check.id}`);
      localPromptIds.add(check.id);
      const promptIdentity = contextualPromptIdentity(pack.contextId, check.id);
      if (contextualPromptIds.has(promptIdentity)) errors.push(`${label}: duplicate contextual prompt identity ${promptIdentity}`);
      contextualPromptIds.add(promptIdentity);
      const normalizedStem = check.stem.toLocaleLowerCase().replace(/\W+/g, " ").trim();
      if (normalizedStems.has(normalizedStem)) errors.push(`${label}: duplicate closed-check wording`);
      normalizedStems.add(normalizedStem);
      if (check.options.length !== 3 || new Set(check.options).size !== 3) errors.push(`${label}/${check.id}: exactly three unique options are required`);
      if (!check.options.includes(check.answer)) errors.push(`${label}/${check.id}: answer must occur in options`);
    }
    for (const prompt of [pack.use, pack.natural]) {
      if (!/\.v\d+$/.test(prompt.id)) errors.push(`${label}: prompt id needs an immutable revision`);
      if (localPromptIds.has(prompt.id)) errors.push(`${label}: duplicate local prompt id ${prompt.id}`);
      localPromptIds.add(prompt.id);
      const promptIdentity = contextualPromptIdentity(pack.contextId, prompt.id);
      if (contextualPromptIds.has(promptIdentity)) errors.push(`${label}: duplicate contextual prompt identity ${promptIdentity}`);
      contextualPromptIds.add(promptIdentity);
    }
    if (pack.use.criteria.length !== 2 || new Set(pack.use.criteria.map((criterion) => criterion.id)).size !== 2) {
      errors.push(`${label}: authored use needs exactly two distinct self-checks`);
    }
    if (JSON.stringify(pack).includes("—")) errors.push(`${label}: em dashes are not allowed in review content`);
    if (/\b(mastered|mastery|guaranteed learned)\b/i.test(JSON.stringify(pack))) {
      errors.push(`${label}: prompt content makes a false learning claim`);
    }
  }

  if (manifest.packs.length !== corpus.size) errors.push(`expected ${corpus.size} prompt packs, received ${manifest.packs.length}`);
  if (errors.length) throw new Error(`Vocabulary review manifest invalid: ${errors.join("; ")}`);
  return manifest;
}
